import {StepDataFrameLineageNode} from 'analysis-tool/data/DatasetLineageUtils';
import {DataFrameColumnRelationship} from 'data-frames/column-relationship-selector/DataFrameColumnRelationshipSelector';
import {DataFrameColumnDef} from 'data-frames/DataFrameTypes';
import {PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {JoinType} from 'pipelines/joins/JoinConstants';
import {AddStepParamsForStep, PreviewStepParams} from 'pipelines/PipelineDAO';
import {PipelineStepConfig} from 'pipelines/PipelineTypes';
import makeUseGlobalClientState from 'ui-library/hooks/global-client-state/makeUseGlobalClientState';
import {MakeFieldsRequired} from 'util/Types';

/**
 * Form state for `CombineDataFramesView`
 * @package
 */
export interface CombineDataFramesState {
  combineMethod: PipelineStepType.JOIN | PipelineStepType.UNION_DATASETS;
  joinType: JoinType;
  leftTable: MakeFieldsRequired<StepDataFrameLineageNode, 'data'> | null;
  rightTable: MakeFieldsRequired<StepDataFrameLineageNode, 'data'> | null;
  relationships: Array<DataFrameColumnRelationship>;
  dataFrameName: string;
  dataFrameNameHasUserEdits: boolean;
}

/** @package */
export const DEFAULT_COMBINE_DATA_FRAMES_STATE: CombineDataFramesState = {
  combineMethod: PipelineStepType.JOIN,
  joinType: 'LEFT_OUTER',
  leftTable: null,
  rightTable: null,
  relationships: [],
  dataFrameName: '',
  dataFrameNameHasUserEdits: false,
};

/** @package */
export const combineDataFramesGlobalState = makeUseGlobalClientState<CombineDataFramesState>(
  DEFAULT_COMBINE_DATA_FRAMES_STATE
);

/** @package */
export function isRelationshipComplete(relationship: DataFrameColumnRelationship): boolean {
  return relationship.leftColumn != null && relationship.rightColumn != null;
}

function getJoinStepParams(
  joinType: JoinType,
  leftTable: MakeFieldsRequired<StepDataFrameLineageNode, 'data'> | null,
  rightTable: MakeFieldsRequired<StepDataFrameLineageNode, 'data'> | null,
  relationships: Array<DataFrameColumnRelationship>,
  dataFrameName: string
): {
  addStepParams: AddStepParamsForStep<PipelineStepType.JOIN, 0> | null;
  previewStepParams: PreviewStepParams<PipelineStepType.JOIN> | null;
} {
  let stepConfig: PipelineStepConfig<PipelineStepType.JOIN, 0>;
  if (leftTable == null || rightTable == null || dataFrameName === '') {
    return {addStepParams: null, previewStepParams: null};
  }

  if (joinType === 'POSITIONAL') {
    stepConfig = {
      dataFrameName,
      joinType,
      joinConditions: [],
    };
  } else {
    if (relationships.length === 0 || relationships.some((r) => !isRelationshipComplete(r))) {
      return {addStepParams: null, previewStepParams: null};
    }
    stepConfig = {
      dataFrameName,
      joinType,
      joinConditions: relationships.map(({leftColumn, rightColumn}) => ({
        leftDataSourceColumn: leftColumn!,
        rightDataSourceColumn: rightColumn!,
      })),
    };
  }
  const addStepParams: AddStepParamsForStep<PipelineStepType.JOIN, 0> = {
    stepType: PipelineStepType.JOIN,
    stepVersion: 0,
    stepConfig,
    stepInput: {
      leftDataFrameId: leftTable.data.dataFrame.id,
      rightDataFrameId: rightTable.data.dataFrame.id,
    },
    stepInputParents: {
      Left: [{stepId: leftTable.step.id, stepDataName: leftTable.data.outputStepData.name}],
      Right: [{stepId: rightTable.step.id, stepDataName: rightTable.data.outputStepData.name}],
    },
  };
  const previewStepParams: PreviewStepParams<PipelineStepType.JOIN, 0> = {
    stepType: PipelineStepType.JOIN,
    stepConfig,
    stepInput: {
      leftDataFrameId: leftTable.data.dataFrame.id,
      rightDataFrameId: rightTable.data.dataFrame.id,
    },
  };
  return {addStepParams, previewStepParams};
}

function getUnionStepParams(
  leftTable: MakeFieldsRequired<StepDataFrameLineageNode, 'data'> | null,
  rightTable: MakeFieldsRequired<StepDataFrameLineageNode, 'data'> | null,
  selectedUnionPrimaryColumns: Array<DataFrameColumnDef | null>,
  selectedUnionSecondaryColumns: Array<DataFrameColumnDef | null>,
  dataFrameName: string
): {
  addStepParams: AddStepParamsForStep<PipelineStepType.UNION_DATASETS, 0> | null;
  previewStepParams: PreviewStepParams<PipelineStepType.UNION_DATASETS> | null;
} {
  if (
    leftTable == null ||
    rightTable == null ||
    selectedUnionPrimaryColumns.length === 0 ||
    selectedUnionSecondaryColumns.length === 0 ||
    selectedUnionPrimaryColumns.some((c) => c == null) ||
    selectedUnionSecondaryColumns.some((c) => c == null) ||
    dataFrameName === ''
  ) {
    return {addStepParams: null, previewStepParams: null};
  }
  const stepConfig: PipelineStepConfig<PipelineStepType.UNION_DATASETS, 0> = {
    dataFrameName,
    primaryDataSourceColumns: selectedUnionPrimaryColumns.map((col) => col!.systemName),
    secondaryDataSourcesColumns: [selectedUnionSecondaryColumns.map((col) => col!.systemName)],
    includeDuplicates: true,
  };
  const addStepParams: AddStepParamsForStep<PipelineStepType.UNION_DATASETS, 0> = {
    stepType: PipelineStepType.UNION_DATASETS,
    stepVersion: 0,
    stepConfig,
    stepInput: {
      primaryDataFrameId: leftTable.data.dataFrame.id,
      secondaryDataFrameId: rightTable.data.dataFrame.id,
    },
    stepInputParents: {
      'Primary data frame': [{stepId: leftTable.step.id, stepDataName: leftTable.data.outputStepData.name}],
      'Secondary data frames': [
        {stepId: rightTable.step.id, stepDataName: rightTable.data.outputStepData.name},
      ],
    },
  };
  const previewStepParams: PreviewStepParams<PipelineStepType.UNION_DATASETS, 0> = {
    stepType: PipelineStepType.UNION_DATASETS,
    stepConfig,
    stepInput: {
      'Primary data frame': leftTable.data.dataFrame.id,
      'Secondary data frames': rightTable.data.dataFrame.id,
    },
  };
  return {addStepParams, previewStepParams};
}

type StepParams =
  | {
      addStepParams: AddStepParamsForStep<PipelineStepType.JOIN, 0> | null;
      previewStepParams: PreviewStepParams<PipelineStepType.JOIN> | null;
    }
  | {
      addStepParams: AddStepParamsForStep<PipelineStepType.UNION_DATASETS, 0> | null;
      previewStepParams: PreviewStepParams<PipelineStepType.UNION_DATASETS> | null;
    };

/**
 * Convert form state to parameters for AnalysisActions.addOrReplaceAndRunAnalysisStep
 * Returns null when state is not ready, else return valid parameters
 * @package
 */
export function getStepParams(
  combineMethod: PipelineStepType.JOIN | PipelineStepType.UNION_DATASETS,
  joinType: JoinType,
  leftTable: MakeFieldsRequired<StepDataFrameLineageNode, 'data'> | null,
  rightTable: MakeFieldsRequired<StepDataFrameLineageNode, 'data'> | null,
  relationships: Array<DataFrameColumnRelationship>,
  selectedUnionPrimaryColumns: Array<DataFrameColumnDef | null>,
  selectedUnionSecondaryColumns: Array<DataFrameColumnDef | null>,
  dataFrameName: string
): StepParams {
  if (combineMethod === PipelineStepType.JOIN) {
    return getJoinStepParams(joinType, leftTable, rightTable, relationships, dataFrameName);
  } else if (combineMethod === PipelineStepType.UNION_DATASETS) {
    return getUnionStepParams(
      leftTable,
      rightTable,
      selectedUnionPrimaryColumns,
      selectedUnionSecondaryColumns,
      dataFrameName
    );
  }
  combineMethod satisfies never;
  return {
    addStepParams: null,
    previewStepParams: null,
  };
}
